import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import * as Turf from '@turf/turf';
import { LeafletDirective } from 'src/app/directives/leaflet/core/leaflet.directive';
import { NominatimResult } from 'src/app/services/geo/model/nominatim';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { LeafletDrawWrapper } from '../leaflet-draw-wrapper';
import { LeafletDrawDirective } from 'src/app/directives/leaflet/core/leaflet-draw.directive';
import { Polygon, LineString, Point } from 'geojson';
import { ModalService } from 'src/app/services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ThemingService } from '@ngx-seatback/utils';
import { Subscription } from 'rxjs';

@Component({
  selector: 'sb-polygon-edit-map',
  templateUrl: './sb-polygon-edit-map.component.html',
  styleUrls: ['./sb-polygon-edit-map.component.scss'],
  standalone: false,
})
export class SbPolygonEditMapComponent implements OnInit, OnDestroy {
  constructor(
    private modal: ModalService,
    public util: UtilService,
    public translate: TranslateService,
    public errorHandler: ErrorService,
    private changeDetector: ChangeDetectorRef,
    public theming: ThemingService
  ) {}

  @Input()
  set initialZoom(val: number) {
    if (this._initialZoom !== val) {
      this._initialZoom = val;
      // this.applyInitialZoom();
    }
  }

  get initialZoom(): number {
    return this._initialZoom;
  }

  get whatIsHere() {
    return this._whatIsHere;
  }

  @Input('whatIsHere')
  set whatIsHere(value: boolean) {
    this._whatIsHere = value;
  }

  get autoFitBounds() {
    return this._autoFitBounds;
  }

  @Input('autoFitBounds')
  set autoFitBounds(value: boolean) {
    this._autoFitBounds = value;
  }

  get centerPoint(): any {
    return this._centerPoint;
  }

  @Input('centerPoint')
  set centerPoint(value: any) {
    if (this._centerPoint !== value) {
      this._centerPoint = value;
      this.panCenterPoint();
    }
  }

  @Input('mapheight')
  get mapheight(): number {
    return this._mapheight;
  }

  set mapheight(value: number) {
    if (this._mapheight !== value) {
      this._mapheight = value;
      if (this._mapheight) {
        this.mHeight = this._mapheight + 'px';
      }

      this.applyResize();
    }
  }

  @Input('mapwidth')
  get mapwidth(): number {
    return this._mapwidth;
  }

  set mapwidth(value: number) {
    if (this._mapwidth !== value) {
      this._mapwidth = value;
      if (this._mapwidth) {
        this.mWidth = this._mapwidth + 'px';
      }

      this.applyResize();
    }
  }

  get layers(): L.Layer[] {
    return this._layers;
  }

  @Input()
  set layers(value: L.Layer[]) {
    this._layers = value;
    this.refreshInternalLayers(false);
  }

  get polygons(): Polygon[] {
    return this._polygons;
  }

  @Input()
  set polygons(value: Polygon[]) {
    this._polygons = value;
    this.refreshInternalLayers(false);
  }

  get negatedPolygons(): Polygon[] {
    return this._negatedPolygons;
  }

  @Input()
  set negatedPolygons(value: Polygon[]) {
    this._negatedPolygons = value;
    this.refreshInternalLayers(false);
  }

  get lineStrings(): LineString[] {
    return this._lineStrings;
  }

  @Input()
  set lineStrings(value: LineString[]) {
    this._lineStrings = value;
    this.refreshInternalLayers(false);
  }

  get points(): Point[] {
    return this._points;
  }

  @Input()
  set points(value: Point[]) {
    this._points = value;
    this.refreshInternalLayers(false);
  }

  get internalLayers(): L.Layer[] {
    return this._internalLayers;
  }

  set internalLayers(val: L.Layer[]) {
    this._internalLayers = val;
    if (this.autoFitBounds) {
      this.fitInternalLayerBounds(true);
    }
  }

  @ViewChild(LeafletDirective) leaflet: LeafletDirective;
  @ViewChild(LeafletDrawDirective) leafletDraw: LeafletDrawDirective;

  iamready: boolean = false;

  @Input()
  opacity: number;

  @Output()
  ready = new EventEmitter<SbPolygonEditMapComponent>();

  @Output()
  mapZoomEnd = new EventEmitter();

  @Output()
  mapMoveEnd = new EventEmitter();

  @Output()
  initialPanReady = new EventEmitter();

  @Input()
  nominatim: boolean = true;

  @Input()
  zoomControl: boolean = true;

  @Input()
  roundedCorners: boolean = false;

  _initialZoom: number;

  @Input()
  showHelp: boolean = true;

  @Input()
  showActiveModeInHelp: boolean = false;

  _whatIsHere: boolean;

  _autoFitBounds: boolean;

  private clickListenerAttached: boolean = false;
  private rightClickListenerAttached: boolean = false;

  dropdownTop?: number;
  dropdownLeft?: number;

  dropdownBottom?: number;
  dropdownRight?: number;

  dropdownDisplay: string = 'none';

  dropDownPxX?: number;
  dropDownPxY?: number;

  dropDownCoordX?: number;
  dropDownCoordY?: number;

  dropDownVisible: boolean = false;

  whatIsHerePopupVisible: boolean = false;
  whatIsHerePopup: L.Popup;

  themingSubscription: Subscription;

  lightLayer = L.tileLayer(this.util.geo.assets.getMap(null, 'light-theme'), {
    id: 'Light',
    maxZoom: 18,
    attribution: '',
  });
  darkLayer = L.tileLayer(this.util.geo.assets.getMap(null, 'dark-theme'), { id: 'Dark', maxZoom: 18, attribution: '' });

  _centerPoint: any;

  private _mapheight: number;

  mHeight: string;

  private _mapwidth: number;

  mWidth: string;

  leafletOptions: any;
  leafletDrawOptions: any;

  searchAddress: string;
  debounceTimer: ReturnType<typeof setTimeout> = null;

  map: L.Map;
  drawControl: L.Control.Draw;

  drawWrapper: LeafletDrawWrapper;

  _layers: L.Layer[];

  @Output()
  layersChange: EventEmitter<L.Layer[]> = new EventEmitter<L.Layer[]>();

  _polygons: Polygon[];

  _negatedPolygons: Polygon[];

  @Output()
  polygonsChange: EventEmitter<Polygon[]> = new EventEmitter<Polygon[]>();
  @Output()
  negatedPolygonsChange: EventEmitter<Polygon[]> = new EventEmitter<Polygon[]>();

  _lineStrings: LineString[];

  @Output()
  lineStringsChange: EventEmitter<LineString[]> = new EventEmitter<LineString[]>();

  _points: Point[];

  @Output()
  pointsChange: EventEmitter<Point[]> = new EventEmitter<Point[]>();

  @Input()
  readOnly: boolean = false;

  _internalLayers: L.Layer[] = [];

  drawnItems: L.FeatureGroup;

  that: SbPolygonEditMapComponent;

  initialPanEmitted: boolean = false;
  initialPanDone: boolean = false;

  drawMode: string = 'DRAW';

  isSmall: boolean = false;

  lastPanPosition: any;
  searchStr: string;

  getIsSmall(): boolean {
    const w = document.documentElement.clientWidth;
    const breakpoint = 1349;
    return w < breakpoint;
  }

  fitBounds(bounds: L.LatLngBoundsExpression, options?: L.FitBoundsOptions) {
    if (this.map && this.leaflet && this.iamready) {
      this.leaflet.doResize();
      this.map.fitBounds(bounds, options);
    } else {
      setTimeout(() => {
        this.fitBounds(bounds, options);
      }, 250);
    }
  }

  setView(center: L.LatLngExpression, zoom: number, options?: L.ZoomPanOptions) {
    if (this.map && this.leaflet && this.iamready) {
      this.map.setView(center, zoom, options);
    } else {
      setTimeout(() => {
        this.setView(center, zoom, options);
      }, 250);
    }
  }

  doInitialPan() {
    if (!this.initialPanDone) {
      if (this.internalLayers && this.internalLayers.length > 0) {
        this.fitInternalLayerBounds();
        this.initialPanDone = true;
        this.emitInitialPanReady();
      } else {
        this.setView(L.latLng(51.3, 9.5), this.initialZoom ? this.initialZoom : 7, { animate: false });
        this.emitInitialPanReady();
      }
    }
  }

  emitInitialPanReady() {
    if (!this.initialPanEmitted) {
      this.initialPanEmitted = true;
      setTimeout(() => {
        this.initialPanReady.emit();
      }, 500);
    }
  }

  normalizeGeoJSONs() {
    if (this._layers) {
      for (let i = 0; i < this._layers.length; i++) {
        if (this._layers[i]) {
          // this._layers[i] = this.util.geo.normalizeGeoJSON(this._layers[i]);
        }
      }
    }
    if (this._polygons) {
      for (let i = 0; i < this._polygons.length; i++) {
        if (this._polygons[i]) {
          this._polygons[i] = this.util.geo.normalizeGeoJSON(this._polygons[i]);
        }
      }
    }
    if (this._negatedPolygons) {
      for (let i = 0; i < this._negatedPolygons.length; i++) {
        if (this._negatedPolygons[i]) {
          this._negatedPolygons[i] = this.util.geo.normalizeGeoJSON(this._negatedPolygons[i]);
        }
      }
    }
    if (this._lineStrings) {
      for (let i = 0; i < this._lineStrings.length; i++) {
        if (this._lineStrings[i]) {
          this._lineStrings[i] = this.util.geo.normalizeGeoJSON(this._lineStrings[i]);
        }
      }
    }
    if (this._points) {
      for (let i = 0; i < this._points.length; i++) {
        if (this._points[i]) {
          this._points[i] = this.util.geo.normalizeGeoJSON(this._points[i]);
        }
      }
    }
  }

  refreshInternalLayers(emitChange: boolean = false) {
    if (this.map && this.leaflet && this.leafletDraw && this.iamready) {
      for (const layer of this.internalLayers) {
        layer.off();
      }

      if (emitChange) {
        this.refreshLayers();
        this.refreshPolygons();
        // this.refreshNegatedPolygons();
        this.refreshLineStrings();
        this.refreshPoints();
      }

      this.normalizeGeoJSONs();

      let layers: L.Layer[] = [];

      layers = this.recalculateLayers();

      this.internalLayers = layers;

      for (const layer of this.internalLayers) {
        layer.off();
        if (!this.readOnly) {
          layer.on('click', (e: L.LeafletEvent) => {
            if (this.drawMode === 'DELETE') {
              this.internalLayers = this.util.geo.removeFromArrayByGeoJson(this.internalLayers, e.layer);
              this.refreshInternalLayers(true);
            }
          });
        }
      }

      if (this.internalLayers.length <= 0) {
        this.setDrawMode();
      }

      this.changeDetector.detectChanges();

      if (emitChange) {
        this.emitChanges();
      }

      this.doInitialPan();
    } else {
      setTimeout(() => {
        this.refreshInternalLayers(emitChange);
      }, 250);
    }

    // setTimeout(() => {
    //   for(let l of this.layers) {
    //     if(l.getTooltip()) {
    //       l.openTooltip();
    //     }
    //   }
    // }, 500);
  }

  recalculateLayers(): L.Layer[] {
    let internalLayers: L.Layer[] = [];
    const layers: L.Layer[] = [];
    const polygons: L.Layer[] = [];
    const negatedPolygons: L.Layer[] = [];
    const lineStrings: L.Layer[] = [];
    const points: L.Layer[] = [];

    /* // Commented out to remove increasing opacity on new area drawings
    if (this._layers) {
      for (const layer of this._layers) {
        layers.push(layer);
      }
    }
    */

    if (this._polygons) {
      for (const polygon of this._polygons) {
        polygons.push(L.geoJSON(polygon, { style: { color: '#3388ff', fillOpacity: 0.2 } }));
      }
    }

    if (this._negatedPolygons) {
      for (const polygon of this._negatedPolygons) {
        negatedPolygons.push(L.geoJSON(polygon, { style: { color: '#ff0000', fillOpacity: 0.2 } }));
      }
    }

    if (this._lineStrings) {
      for (const lineString of this._lineStrings) {
        lineStrings.push(L.geoJSON(lineString));
      }
    }

    if (this._points) {
      for (const point of this._points) {
        points.push(
          L.geoJSON(point, {
            pointToLayer: (feature, latlng) => {
              return L.marker(latlng, { icon: this.util.geo.assets.pin });
            },
          })
        );
      }
    }

    internalLayers = internalLayers.concat(layers).concat(polygons).concat(negatedPolygons).concat(lineStrings).concat(points);
    return internalLayers;
  }

  refreshLayers() {
    this._layers = [];
    for (const layer of this.internalLayers) {
      this._layers.push(layer);
    }
  }

  refreshPolygons() {
    this._polygons = [];
    for (const layer of this.internalLayers) {
      let geojson: any = (layer as L.GeoJSON).toGeoJSON();

      geojson = this.util.geo.normalizeGeoJSON(geojson);

      if (geojson.geometry?.type === 'Polygon') {
        this._polygons.push(geojson.geometry);
      }
    }
  }

  refreshNegatedPolygons() {
    this._negatedPolygons = [];
    for (const layer of this.internalLayers) {
      let geojson: any = (layer as L.GeoJSON).toGeoJSON();

      geojson = this.util.geo.normalizeGeoJSON(geojson);

      if (geojson.geometry?.type === 'Polygon') {
        this._negatedPolygons.push(geojson.geometry);
      }
    }
  }

  refreshLineStrings() {
    this._lineStrings = [];
    for (const layer of this.internalLayers) {
      let geojson: any = (layer as L.GeoJSON).toGeoJSON();

      geojson = this.util.geo.normalizeGeoJSON(geojson);

      if (geojson.geometry?.type === 'LineString') {
        this._lineStrings.push(geojson.geometry);
      }
    }
  }

  refreshPoints() {
    this._points = [];
    for (const layer of this.internalLayers) {
      let geojson: any = (layer as L.GeoJSON).toGeoJSON();

      geojson = this.util.geo.normalizeGeoJSON(geojson);

      if (geojson.geometry?.type === 'Point') {
        this._points.push(geojson.geometry);
      }
    }
  }

  emitChanges() {
    this.polygonsChange.emit(this._polygons);
    this.negatedPolygonsChange.emit(this._negatedPolygons);
    this.lineStringsChange.emit(this._lineStrings);
    this.pointsChange.emit(this._points);
  }

  attachClickListener() {
    if (this.map && this.leaflet && this.iamready) {
      if (!this.clickListenerAttached) {
        this.clickListenerAttached = true;
        this.map.on('click', e => {
          this.onClick(e);
        });
      }
    } else {
      setTimeout(() => {
        this.attachClickListener();
      }, 250);
    }
  }

  attachRightClickListener() {
    if (this.map && this.leaflet && this.iamready) {
      if (!this.rightClickListenerAttached) {
        this.rightClickListenerAttached = true;
        this.map.on('contextmenu', e => {
          this.onRightClick(e);
        });
      }
    } else {
      setTimeout(() => {
        this.attachRightClickListener();
      }, 250);
    }
  }

  onClick(e: L.LeafletEvent) {
    if (this.whatIsHere && this.whatIsHerePopupVisible) {
      this.hideWhatIsHerePopup();
      return;
    }
    if (this.whatIsHere && this.dropDownVisible) {
      this.hideDropdown();
      return;
    }
  }

  onRightClick(e: L.LeafletEvent) {
    if (this.whatIsHere) {
      const latlng: L.LatLng = L.latLng(e['latlng']['lat'], e['latlng']['lng']);
      this.dropDownPxX = e['containerPoint']['x'];
      this.dropDownPxY = e['containerPoint']['y'];
      this.dropDownCoordX = latlng.lng;
      this.dropDownCoordY = latlng.lat;

      this.showDropdown();
    }
  }

  showDropdown() {
    this.dropdownTop = null;
    this.dropdownLeft = null;
    this.dropdownBottom = null;
    this.dropdownRight = null;

    const mapWidth: number = this.map.getSize().x;
    const mapHeight: number = this.map.getSize().y;

    const useLeft: boolean = this.dropDownPxX <= mapWidth / 2;
    const useTop: boolean = this.dropDownPxY <= mapHeight / 2;

    if (useLeft) {
      this.dropdownLeft = this.dropDownPxX + 1;
    } else {
      this.dropdownRight = mapWidth - this.dropDownPxX - 1;
    }
    if (useTop) {
      this.dropdownTop = this.dropDownPxY + 1;
    } else {
      this.dropdownBottom = mapHeight - this.dropDownPxY - 1;
    }

    this.dropdownDisplay = 'block';

    this.dropDownVisible = true;

    this.changeDetector.detectChanges();
  }

  hideDropdown() {
    if (this.dropDownVisible) {
      this.dropdownTop = null;
      this.dropdownLeft = null;
      this.dropdownBottom = null;
      this.dropdownRight = null;
      this.dropdownDisplay = 'none';

      this.dropDownVisible = false;

      this.changeDetector.detectChanges();
    }
  }

  hideWhatIsHerePopup() {
    if (this.whatIsHerePopupVisible) {
      if (this.whatIsHerePopup) {
        this.whatIsHerePopup.remove();
      }
      this.whatIsHerePopup = null;
      this.whatIsHerePopupVisible = false;
    }
    this.changeDetector.detectChanges();
  }

  callDropdownAction(action) {
    action(this.that);
    this.hideDropdown();
  }

  options() {
    return [
      {
        text: this.translate.instant('lang.map.whatIsHere'),
        icon: 'search',
        enabled: true,
        action: (that: SbPolygonEditMapComponent) => {
          that.showWhatIsHere();
        },
      },
    ];
  }

  showWhatIsHere() {
    this.util.geo.geocoder.coordinatesToAddress({ x: this.dropDownCoordX, y: this.dropDownCoordY }).subscribe((e: NominatimResult[]) => {
      let html = '';
      html += this.util.geo.whatIsHereDiv(e, { y: this.dropDownCoordY, x: this.dropDownCoordX });

      this.whatIsHerePopup = L.popup({ closeButton: false })
        .setLatLng([this.dropDownCoordY, this.dropDownCoordX])
        .setContent(html)
        .openOn(this.map);
      this.whatIsHerePopupVisible = true;
      this.changeDetector.detectChanges();
    });
  }

  perc2color(perc: number) {
    const turnaround = 100 - perc;
    let r: number;
    let g: number;
    const b = 0;
    if (turnaround < 50) {
      r = 255;
      g = Math.round(5.1 * turnaround);
    } else {
      g = 255;
      r = Math.round(510 - 5.1 * turnaround);
    }
    const h = r * 0x10000 + g * 0x100 + b * 0x1;
    return '#' + ('000000' + h.toString(16)).slice(-6);
  }

  panCenterPoint() {
    if (this.centerPoint) {
      this.panTo(this.centerPoint.y, this.centerPoint.x);
    }
  }

  panTo(pY, pX, pAnimate = false) {
    if (this.map && this.iamready) {
      let dopan: boolean = true;
      if (this.lastPanPosition) {
        if (this.lastPanPosition.x === pX && this.lastPanPosition.y === pY) {
          dopan = false;
        }
      }
      if (dopan) {
        this.leaflet.doResize();
        this.lastPanPosition = { y: pY, x: pX };
        this.map.setView(new L.LatLng(pY, pX), this.map.getZoom(), { animate: pAnimate });
        setTimeout(() => {
          this.emitInitialPanReady();
        }, 250);
      }
    } else {
      setTimeout(() => {
        this.panTo(pY, pX);
      }, 250);
    }
  }

  applyInitialZoom() {
    if (this.initialZoom) {
      if (this.map && this.iamready) {
        this.map.setZoom(this.initialZoom);
        // this.applyResize();
      } else {
        setTimeout(this.applyInitialZoom, 250);
      }
    }
  }

  zoomIn() {
    if (this.map && this.iamready) {
      this.map.zoomIn();
    } else {
      setTimeout(this.zoomIn, 250);
    }
  }

  zoomOut() {
    if (this.map && this.iamready) {
      this.map.zoomOut();
    } else {
      setTimeout(this.zoomOut, 250);
    }
  }

  fitAreaBounds() {
    if (this.internalLayers.length > 0) {
      const group: L.FeatureGroup = new L.FeatureGroup(this.internalLayers);
      this.fitBounds(group.getBounds(), { animate: false });
    } else {
      this.setView(L.latLng(51.3, 9.5), this.initialZoom ? this.initialZoom : 7, { animate: false });
    }
  }

  search($event) {
    this.searchAddress = $event;
    this.searchDebounced();
  }

  searchDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.doSearch();
    }, 500);
  }

  doSearch() {
    this.util.geo.geocoder.searchAddress(this.searchAddress).subscribe(
      result => {
        if (result && result.length && result.length > 0 && this.map) {
          const res: NominatimResult = result[0];
          if (res.boundingbox && res.boundingbox.length >= 4) {
            this.fitBounds(this.util.geo.nominatimBoundingBoxToLatLngBounds(res.boundingbox), { animate: false });
          } else {
            this.panTo(res.lat, res.lon);
          }
        }
      },
      error => {
        this.errorHandler.errorHandler(error);
      }
    );
  }

  onMapReady(map: L.Map) {
    this.map = map;

    this.applyResize();
  }

  onDrawReady(drawControl: L.Control.Draw) {
    this.drawControl = drawControl;

    const hideToolbar: boolean = true;

    this.drawWrapper = new LeafletDrawWrapper(this.drawControl, hideToolbar);

    this.applyDrawListeners();

    this.applyResize();
  }

  applyDrawListeners() {
    if (!this.readOnly) {
      if (this.leafletDraw) {
        this.leafletDraw.onDrawCreated.subscribe((e: L.DrawEvents.Created) => {
          let theLayer: any = e.layer;
          this.drawnItems.removeLayer(theLayer);

          let isKinky: boolean = false;
          let isLine: boolean = false;
          let isPoint: boolean = false;

          isLine = e.layerType === 'polyline';

          isPoint = e.layerType === 'marker';

          if (!isLine && !isPoint) {
            if (theLayer.layerType === 'rectangle') {
              theLayer = L.polygon(theLayer._latlngs);
            }
            if (theLayer._mRadius) {
              const latlng: L.LatLng = theLayer._latlng;
              const radius: number = theLayer._mRadius;
              theLayer = L.geoJSON(this.util.geo.circle(this.util.geo.createPoint(latlng.lng, latlng.lat), radius, 32));
            }
            try {
              if (Turf.kinks(this.util.geo.normalizeGeoJSON(theLayer.toGeoJSON())).features.length) {
                isKinky = true;
              }
            } catch (error) {
              this.errorHandler.errorHandler(error);
            }
          }

          this.drawnItems.clearLayers();

          if (isKinky) {
            this.modal.alert(
              'Self-intersection not supported',
              'Usage of self-intersecting polygons is not supported!',
              this.translate.instant('lang.globals.ok')
            );
          } else {
            switch (this.drawMode) {
              case 'DRAW':
                this.internalLayers.push(L.geoJSON(theLayer.toGeoJSON()));
                break;
              case 'CUT':
                if (!isLine && !isPoint) {
                  const rmlayers: L.Layer[] = [];
                  const addlayers: L.Layer[] = [];
                  for (const layer of this.internalLayers) {
                    let geojson: any = (layer as L.GeoJSON).toGeoJSON();
                    let geojsonnew: any = theLayer.toGeoJSON();

                    geojson = this.util.geo.normalizeGeoJSON(geojson);

                    geojsonnew = this.util.geo.normalizeGeoJSON(geojsonnew);
                    try {
                      if (
                        (Turf.booleanOverlap(geojson, geojsonnew) || Turf.booleanContains(geojson, geojsonnew)) &&
                        !Turf.booleanContains(geojsonnew, geojson)
                      ) {
                        if (this.canDeleteFromIntersection(layer)) {
                          geojsonnew = Turf.difference(Turf.featureCollection([geojson, geojsonnew]));
                          const newLayer = L.geoJSON(geojsonnew);
                          rmlayers.push(layer);
                          addlayers.push(L.geoJSON(newLayer.toGeoJSON()));
                        }
                      }
                    } catch (e) {}
                    try {
                      if (Turf.booleanContains(geojsonnew, geojson)) {
                        try {
                          if (this.canDeleteFromIntersection(layer)) {
                            rmlayers.push(layer);
                          }
                        } catch (err) {}
                      }
                    } catch (e) {}
                  }

                  for (const rmlayer of rmlayers) {
                    const index = this.internalLayers.indexOf(rmlayer);
                    if (index !== -1) {
                      this.internalLayers.splice(index, 1);
                    }
                  }

                  for (const addlayer of addlayers) {
                    this.internalLayers.push(addlayer);
                  }
                }
                break;
            }
          }
          this.drawWrapper.cancel();
          this.refreshInternalLayers(true);
        });
      } else {
        setTimeout(() => {
          this.applyDrawListeners();
        }, 250);
      }
    }
  }

  canDeleteFromIntersection(layer: L.Layer) {
    try {
      const templayer = this.util.geo.normalizeGeoJSON((layer as L.GeoJSON).toGeoJSON());
      if (templayer?.geometry?.type !== 'Point' && templayer?.geometry?.type !== 'LineString') {
        return true;
      }
      return false;
    } catch (err) {}
    return true;
  }

  setDrawMode() {
    this.drawMode = 'DRAW';
  }

  setCutMode() {
    this.drawMode = 'CUT';
    if (this.isDrawToolActive()) {
      if (this.drawWrapper.currentHandler === this.drawWrapper.polylineHandler) {
        this.drawWrapper.cancel();
      } else if (this.drawWrapper.currentHandler === this.drawWrapper.markerHandler) {
        this.drawWrapper.cancel();
      }
    }
  }

  setDeleteMode() {
    if (this.internalLayers && this.internalLayers.length > 0) {
      this.drawMode = 'DELETE';
      this.drawWrapper.cancel();
    }
  }

  getModeButtonClass(mode: string) {
    let classes = 'actionButtonMinWidth btn';

    if (mode === 'DRAW') {
      if (this.drawMode === mode) {
        classes += ' active btn-success';
      } else {
        classes += ' btn-info';
      }
    }

    if (mode === 'CUT') {
      if (this.drawMode === mode) {
        classes += ' active btn-warning';
      } else {
        classes += ' btn-info';
      }
    }

    if (mode === 'DELETE') {
      if (this.drawMode === mode) {
        classes += ' active btn-danger';
      } else {
        classes += ' btn-info';
      }
      if (this.internalLayers && this.internalLayers.length <= 0) {
        classes += ' disabled';
        return classes;
      }
    }

    return classes;
  }

  toggleDrawPolygon() {
    if (this.drawWrapper.currentHandler === this.drawWrapper.polygonHandler) {
      this.drawWrapper.cancel();
    } else {
      if (this.drawMode !== 'DELETE') {
        this.drawWrapper.drawPolygon();
      }
    }
  }

  toggleDrawRectangle() {
    if (this.drawWrapper.currentHandler === this.drawWrapper.rectangleHandler) {
      this.drawWrapper.cancel();
    } else {
      if (this.drawMode !== 'DELETE') {
        this.drawWrapper.drawRectangle();
      }
    }
  }

  toggleDrawCircle() {
    if (this.drawWrapper.currentHandler === this.drawWrapper.circleHandler) {
      this.drawWrapper.cancel();
    } else {
      if (this.drawMode !== 'DELETE') {
        this.drawWrapper.drawCircle();
      }
    }
  }

  toggleDrawLineString() {
    if (this.drawWrapper.currentHandler === this.drawWrapper.polylineHandler) {
      this.drawWrapper.cancel();
    } else {
      if (this.drawMode !== 'DELETE' && this.drawMode !== 'CUT') {
        this.drawWrapper.drawPolyline();
      }
    }
  }

  toggleDrawPosition() {
    if (this.drawWrapper.currentHandler === this.drawWrapper.markerHandler) {
      this.drawWrapper.cancel();
    } else {
      if (this.drawMode !== 'DELETE' && this.drawMode !== 'CUT') {
        this.drawWrapper.drawMarker();
      }
    }
  }

  getDrawButtonClass(type: string) {
    let classes = 'actionButtonMinWidth btn';

    if (this.drawMode === 'DELETE') {
      classes += ' disabled';
      return classes;
    }

    if (this.drawMode === 'CUT') {
      if (type === 'linestring' || type === 'position') {
        classes += ' disabled';
        return classes;
      }
    }

    switch (type) {
      case 'polygon':
        if (this.drawWrapper.currentHandler === this.drawWrapper.polygonHandler) {
          classes += ' active btn-primary';
        } else {
          classes += ' btn-info';
        }
        break;
      case 'rectangle':
        if (this.drawWrapper.currentHandler === this.drawWrapper.rectangleHandler) {
          classes += ' active btn-primary';
        } else {
          classes += ' btn-info';
        }
        break;
      case 'circle':
        if (this.drawWrapper.currentHandler === this.drawWrapper.circleHandler) {
          classes += ' active btn-primary';
        } else {
          classes += ' btn-info';
        }
        break;
      case 'linestring':
        if (this.drawWrapper.currentHandler === this.drawWrapper.polylineHandler) {
          classes += ' active btn-primary';
        } else {
          classes += ' btn-info';
        }
        break;
      case 'position':
        if (this.drawWrapper.currentHandler === this.drawWrapper.markerHandler) {
          classes += ' active btn-primary';
        } else {
          classes += ' btn-info';
        }
        break;
    }

    return classes;
  }

  isDrawToolActive() {
    return (
      this.drawWrapper.currentHandler === this.drawWrapper.polygonHandler ||
      this.drawWrapper.currentHandler === this.drawWrapper.rectangleHandler ||
      this.drawWrapper.currentHandler === this.drawWrapper.circleHandler ||
      this.drawWrapper.currentHandler === this.drawWrapper.polylineHandler ||
      this.drawWrapper.currentHandler === this.drawWrapper.markerHandler
    );
  }

  getCurrentDrawToolName() {
    if (this.drawWrapper.currentHandler === this.drawWrapper.polygonHandler) {
      return this.translate.instant('lang.globals.map.polygon');
    } else if (this.drawWrapper.currentHandler === this.drawWrapper.rectangleHandler) {
      return this.translate.instant('lang.globals.map.rectangle');
    } else if (this.drawWrapper.currentHandler === this.drawWrapper.circleHandler) {
      return this.translate.instant('lang.globals.map.circle');
    } else if (this.drawWrapper.currentHandler === this.drawWrapper.polylineHandler) {
      return this.translate.instant('lang.globals.map.line');
    } else if (this.drawWrapper.currentHandler === this.drawWrapper.markerHandler) {
      return this.translate.instant('lang.globals.map.position');
    } else {
      return this.translate.instant('lang.globals.map.no');
    }
  }

  getCurrentDrawToolClass() {
    if (this.drawWrapper.currentHandler === this.drawWrapper.polygonHandler) {
      return 'primary';
    } else if (this.drawWrapper.currentHandler === this.drawWrapper.rectangleHandler) {
      return 'primary';
    } else if (this.drawWrapper.currentHandler === this.drawWrapper.circleHandler) {
      return 'primary';
    } else {
      return 'info';
    }
  }

  applyResize() {
    if (this.map && this.leaflet && this.iamready) {
      this.leaflet.delayResize();
    } else {
      setTimeout(() => {
        this.applyResize();
      }, 1000);
    }
  }

  ngOnInit() {
    this.isSmall = this.getIsSmall();
    window.onresize = () => {
      this.isSmall = this.getIsSmall();
    };
    this.leafletOptions = {
      layers:
        // tileLayer('https://tile.seatback.de/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '' })
        // L.tileLayer(this.util.geo.assets.getMap(), { maxZoom: 18, attribution: '' }),
        this.theming.currentTheme === 'dark-theme' ? [this.darkLayer] : [this.lightLayer],
      attributionControl: false,
      zoom: this.initialZoom ? this.initialZoom : 7,
      zoomControl: false,
      center: L.latLng(51.3, 9.5), // Kassel (Kalender Standard fuer Deutschland)
    };

    this.themingSubscription = this.theming.theme.subscribe((theme: string) => {
      this.switchLayer(theme);
    });

    this.drawnItems = L.featureGroup();

    this.leafletDrawOptions = {
      position: 'topleft',
      draw: {
        rectangle: { showArea: false },
        polygon: { allowIntersection: false },
        marker: this.util.geo.assets.pin,
      },
      edit: {
        featureGroup: this.drawnItems,
      },
    };

    this.that = this;

    this.iamready = true;

    this.ready.emit(this);

    this.attachClickListener();
    this.attachRightClickListener();
  }

  switchLayer(selectedTheme: string) {
    if (this.map) {
      if (selectedTheme === 'dark-theme') {
        if (this.map.hasLayer(this.lightLayer)) {
          this.map.removeLayer(this.lightLayer);
        }
        this.map.addLayer(this.darkLayer);
      } else {
        if (this.map.hasLayer(this.darkLayer)) {
          this.map.removeLayer(this.darkLayer);
        }
        this.map.addLayer(this.lightLayer);
      }
    }
  }

  ngOnDestroy() {
    this.themingSubscription.unsubscribe();
  }

  emitMapZoomEnd() {
    this.mapZoomEnd.emit();
  }

  emitMapMoveEnd() {
    this.mapMoveEnd.emit();
  }

  getBoundingRectangle(): L.LatLngBounds {
    if (this.map) {
      return this.map.getBounds();
    }
    return null;
  }

  fitInternalLayerBounds(animate?: boolean) {
    const group: L.FeatureGroup = new L.FeatureGroup(this._internalLayers);
    this.fitBounds(group.getBounds(), { animate: !animate ? false : animate });
  }
}
